/**
 * The ONE element/attribute matrix for template image references in HTML, plus
 * a quote-aware scanner that reports each URL's byte span so a rewrite can
 * SPLICE rather than parse-and-reserialise.
 *
 * Why a scanner and not a DOM: linkedom/jsdom round-trip only 75.0% of the
 * 10,422-section corpus byte-exactly — they self-close SVG voids, drop empty
 * attributes and flatten `&amp;` to a bare `&` inside URLs. Section HTML that
 * goes back into the renderer must come out byte-identical everywhere a URL was
 * not replaced.
 *
 * Why not the regex in `parsers/repair-dead-section-images.js`: it truncates a
 * tag at the first `>` even inside a quoted value, handles quoted values only,
 * matches `data-src` when it means `src` (its attribute alternation is not
 * anchored to a word boundary), and splits srcset on bare commas — which
 * corrupts any candidate URL containing one. That parser is fine for the corpus
 * it was measured against; it is not a general tool, and this is.
 *
 * The matrix below is the UNION of the two places that already disagreed:
 * `export/asset-resolver.js` (collectHtmlAssetUrls) and
 * `parsers/vendor-template-assets.js` (extractTemplateAssetUrls). Extraction for
 * VENDORING deliberately stays with the vendorer's function so the two cannot
 * disagree about what is worth capturing; `assertRewritableAreCapturable` in the
 * test suite pins the direction that matters — anything we are willing to
 * rewrite must be something the vendorer would have captured, or the rewrite
 * targets bytes that were never stored and can never resolve.
 */

/**
 * Single-URL attributes, keyed by the element they are image material on.
 * `'*'` means any element: a deferred-background convention can be stamped on a
 * div as readily as on an img, and those references are just as structural —
 * lite-video posters and JS-applied parallax backgrounds visibly break when
 * their original URL is left behind.
 */
export const URL_ATTRS_BY_TAG = {
  img: ['src', 'data-src'],
  source: ['src'], // gated to <picture> descendants — see scanHtmlAssetRefs
  input: ['src'], // gated to type="image"
  image: ['href', 'xlink:href'], // SVG <image>
  '*': ['poster', 'data-poster', 'data-placeholder', 'data-bg', 'data-bg-webp', 'data-bg-jpeg']
};

/** Comma-separated candidate lists, same gating rules. */
export const SRCSET_ATTRS_BY_TAG = {
  img: ['srcset', 'data-srcset'],
  source: ['srcset', 'data-srcset']
};

/**
 * Deliberately EXCLUDED, and why — this half of the table is the part that
 * rots silently, so it is written down rather than implied:
 *   video/audio <source>  — media streams, not images. The export resolver
 *                           scopes <source> to `picture source` for exactly
 *                           this reason; we match that.
 *   script/iframe src     — not image material.
 *   a[href], link[href]   — navigation and document-level resources. `link`
 *                           IS captured by the vendorer (favicons, preloads)
 *                           but never appears inside a section's HTML.
 *   meta[content]         — og:image/twitter:image are page-level; same.
 *   fonts, video          — out of scope for this pass by decision.
 */
export const EXCLUDED_FROM_REWRITE = Object.freeze([
  'video source',
  'audio source',
  'script[src]',
  'iframe[src]',
  'a[href]',
  'link[href]',
  'meta[content]'
]);

const IMAGE_EXT_RE = /\.(?:jpe?g|png|gif|webp|avif|svg|ico|bmp)(?:[?#]|$)/i;

/** CodeStitch's CDN serves extensionless image URLs; the index gate accepts them. */
const EXTENSIONLESS_IMAGE_HOST_RE = /csimg|csimages/i;

/**
 * A value worth attempting a lookup on. Deliberately permissive about the URL
 * SHAPE (relative, root-relative, protocol-relative and absolute all resolve
 * downstream) and strict about it being an image at all, because the deferred
 * `data-*` attributes carry non-image payloads on plenty of sites.
 */
export function looksLikeImageRef(value) {
  const raw = String(value || '').trim();
  if (!raw) return false;
  if (/^(?:data:|blob:|javascript:|mailto:|tel:|#)/i.test(raw)) return false;
  if (raw.startsWith('pb-asset://') || raw.startsWith('pb-vendored://')) return false;
  return IMAGE_EXT_RE.test(raw) || EXTENSIONLESS_IMAGE_HOST_RE.test(raw);
}

const VOID_RAW_TEXT = new Set(['script', 'style', 'textarea', 'title']);

function isSpace(ch) {
  return ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r' || ch === '\f';
}

/**
 * Split a srcset value into candidates, reporting each URL's [start,end) offset
 * WITHIN the attribute value.
 *
 * Follows the HTML spec's parse order rather than splitting on commas: the URL
 * token is read to whitespace, and only then are trailing commas stripped. A
 * bare `value.split(',')` corrupts every candidate whose URL contains a comma
 * (Cloudinary and Imgix transform segments routinely do).
 */
export function parseSrcsetSpans(value) {
  const s = String(value || '');
  const out = [];
  let i = 0;
  while (i < s.length) {
    while (i < s.length && (isSpace(s[i]) || s[i] === ',')) i += 1;
    if (i >= s.length) break;
    const start = i;
    while (i < s.length && !isSpace(s[i])) i += 1;
    let end = i;
    // Trailing commas belong to the separator, not to the URL.
    const commaTerminated = end > start && s[end - 1] === ',';
    while (end > start && s[end - 1] === ',') end -= 1;
    if (end > start) out.push({ start, end, url: s.slice(start, end) });
    // A comma-terminated URL ENDS its candidate — it has no descriptor, so the
    // next token is the next URL. Skipping to the following comma here would
    // swallow it whole (`a.jpg, b.jpg 2x` would yield one candidate, not two).
    if (!commaTerminated) {
      while (i < s.length && s[i] !== ',') i += 1;
    }
  }
  return out;
}

/** `url(...)` occurrences inside a style attribute or <style> block. */
export function parseCssUrlSpans(value) {
  const s = String(value || '');
  const out = [];
  const re = /url\(\s*(['"]?)([^'")]*)\1\s*\)/gi;
  let m;
  while ((m = re.exec(s))) {
    const quote = m[1];
    const inner = m[2];
    if (!inner) continue;
    const start = m.index + m[0].indexOf(inner, quote ? m[0].indexOf(quote) : 0);
    out.push({ start, end: start + inner.length, url: inner });
  }
  return out;
}

function attrsForTag(tag, table) {
  return [...(table[tag] || []), ...(table['*'] || [])];
}

/**
 * Locate every rewritable image URL in `html`.
 *
 * Returns `[{ attr, tag, url, start, end }]` where [start,end) are offsets into
 * the ORIGINAL string, ascending. `url` is the raw source text — entity
 * references are NOT decoded here, because the caller must be able to write the
 * exact original bytes back when a lookup misses.
 */
export function scanHtmlAssetRefs(html) {
  const s = String(html || '');
  const lower = s.toLowerCase();
  const refs = [];
  let pictureDepth = 0;
  let i = 0;

  while (i < s.length) {
    const lt = s.indexOf('<', i);
    if (lt === -1) break;

    if (s.startsWith('<!--', lt)) {
      const close = s.indexOf('-->', lt + 4);
      i = close === -1 ? s.length : close + 3;
      continue;
    }
    if (s[lt + 1] === '!' || s[lt + 1] === '?') {
      const close = s.indexOf('>', lt);
      i = close === -1 ? s.length : close + 1;
      continue;
    }

    const closing = s[lt + 1] === '/';
    let p = lt + (closing ? 2 : 1);
    const nameStart = p;
    while (p < s.length && /[A-Za-z0-9:-]/.test(s[p])) p += 1;
    const tag = s.slice(nameStart, p).toLowerCase();
    if (!tag) {
      i = lt + 1;
      continue;
    }

    if (closing) {
      if (tag === 'picture' && pictureDepth > 0) pictureDepth -= 1;
      const close = s.indexOf('>', p);
      i = close === -1 ? s.length : close + 1;
      continue;
    }

    // Attribute walk. This is the part a regex cannot do: `>` inside a quoted
    // value does not end the tag.
    const attrs = new Map();
    let selfClosing = false;
    while (p < s.length) {
      while (p < s.length && isSpace(s[p])) p += 1;
      if (p >= s.length) break;
      if (s[p] === '>') {
        p += 1;
        break;
      }
      if (s[p] === '/' && s[p + 1] === '>') {
        selfClosing = true;
        p += 2;
        break;
      }
      const aStart = p;
      while (p < s.length && !isSpace(s[p]) && s[p] !== '=' && s[p] !== '>' && s[p] !== '/') p += 1;
      const attrName = s.slice(aStart, p).toLowerCase();
      while (p < s.length && isSpace(s[p])) p += 1;
      if (s[p] !== '=') {
        if (attrName) attrs.set(attrName, null);
        continue;
      }
      p += 1;
      while (p < s.length && isSpace(s[p])) p += 1;
      const quote = s[p] === '"' || s[p] === "'" ? s[p] : '';
      let vStart;
      let vEnd;
      if (quote) {
        p += 1;
        vStart = p;
        while (p < s.length && s[p] !== quote) p += 1;
        vEnd = p;
        p += 1; // closing quote
      } else {
        vStart = p;
        while (p < s.length && !isSpace(s[p]) && s[p] !== '>') p += 1;
        vEnd = p;
      }
      if (attrName) attrs.set(attrName, { start: vStart, end: vEnd, value: s.slice(vStart, vEnd) });
    }

    if (tag === 'picture' && !selfClosing) pictureDepth += 1;

    // Element gating.
    const isPictureSource = tag === 'source' && pictureDepth > 0;
    const isImageInput =
      tag === 'input' && String(attrs.get('type')?.value || '').toLowerCase() === 'image';
    const gateOk =
      tag !== 'source' && tag !== 'input'
        ? true
        : tag === 'source'
          ? isPictureSource
          : isImageInput;

    if (gateOk) {
      for (const attr of attrsForTag(tag, URL_ATTRS_BY_TAG)) {
        const slot = attrs.get(attr);
        if (!slot || !slot.value) continue;
        if (!looksLikeImageRef(slot.value)) continue;
        refs.push({ tag, attr, url: slot.value, start: slot.start, end: slot.end });
      }
      for (const attr of attrsForTag(tag, SRCSET_ATTRS_BY_TAG)) {
        const slot = attrs.get(attr);
        if (!slot || !slot.value) continue;
        for (const span of parseSrcsetSpans(slot.value)) {
          if (!looksLikeImageRef(span.url)) continue;
          refs.push({
            tag,
            attr,
            url: span.url,
            start: slot.start + span.start,
            end: slot.start + span.end
          });
        }
      }
    }

    // `style` is image material on every element.
    const styleSlot = attrs.get('style');
    if (styleSlot && styleSlot.value) {
      for (const span of parseCssUrlSpans(styleSlot.value)) {
        if (!looksLikeImageRef(span.url)) continue;
        refs.push({
          tag,
          attr: 'style',
          url: span.url,
          start: styleSlot.start + span.start,
          end: styleSlot.start + span.end
        });
      }
    }

    // `<style>` is raw text to the HTML parser, but its CSS url() values are
    // still runtime image references. Scan the text span directly so replacing
    // one URL preserves every byte of the surrounding HTML/CSS.
    if (tag === 'style' && !selfClosing) {
      const close = lower.indexOf('</style', p);
      const cssEnd = close === -1 ? s.length : close;
      const css = s.slice(p, cssEnd);
      for (const span of parseCssUrlSpans(css)) {
        if (!looksLikeImageRef(span.url)) continue;
        refs.push({
          tag,
          attr: 'style-block',
          url: span.url,
          start: p + span.start,
          end: p + span.end
        });
      }
      i = close === -1 ? s.length : close;
      continue;
    }

    // Raw-text elements swallow markup until their close tag.
    if (VOID_RAW_TEXT.has(tag) && !selfClosing) {
      const close = lower.indexOf(`</${tag}`, p);
      i = close === -1 ? s.length : close;
      continue;
    }
    i = p;
  }

  refs.sort((a, b) => a.start - b.start);
  return refs;
}

/**
 * Resolve every image reference in `html` to its vendored copy where this
 * domain's bundle has one; leave the rest byte-identical.
 *
 * `toAssetUrl(logicalKey)` is injected rather than imported so this module stays
 * free of the asset layer — the canvas hands it `getAssetURLSync`, the saved
 * preview hands it the same, and a test hands it an identity function.
 *
 * `vendoredAssets` must be THIS domain's map. A global union would report
 * coverage the domain does not have and rewrite to a file its bundle never
 * received; ownership is the pair, always.
 */
export function rewriteHtmlAssetUrlsPreferVendored(html, sourceDomain, vendoredAssets, deps = {}) {
  const s = String(html || '');
  const { lookupRel, toAssetUrl, bundleRoot } = deps;
  if (!s || !sourceDomain || typeof lookupRel !== 'function' || typeof toAssetUrl !== 'function') {
    return s;
  }
  const map = vendoredAssets && typeof vendoredAssets === 'object' ? vendoredAssets : null;
  if (!map || Object.keys(map).length === 0) return s;

  const domain = String(sourceDomain)
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/\/+$/, '');
  if (!domain) return s;
  const root = typeof bundleRoot === 'function' ? bundleRoot(domain) : '';
  if (!root) return s;

  return rewriteHtmlAssetRefs(s, (rawUrl) => {
    // Decode entity references for the LOOKUP only. The manifest is keyed by the
    // URL the crawler fetched, which carries a literal `&`; the source bytes
    // carry `&amp;`. Decoding the source in place would be a silent rewrite of
    // markup we may then decline to replace.
    const forLookup = rawUrl.replace(/&amp;/gi, '&');
    const rel = lookupRel(forLookup, domain, map);
    if (!rel) return null;
    return toAssetUrl(`${root}/${rel}`);
  });
}

/**
 * Rewrite every image reference `resolve` returns a replacement for.
 *
 * `resolve(url) -> string | null | undefined`. A falsy answer leaves the
 * original bytes EXACTLY as they were — a hotlink is the fallback, never a
 * blank. Splices run back-to-front so earlier offsets stay valid.
 */
export function rewriteHtmlAssetRefs(html, resolve) {
  const s = String(html || '');
  if (!s || typeof resolve !== 'function') return s;
  const refs = scanHtmlAssetRefs(s);
  if (refs.length === 0) return s;

  let out = s;
  for (let i = refs.length - 1; i >= 0; i -= 1) {
    const ref = refs[i];
    let replacement;
    try {
      replacement = resolve(ref.url, ref);
    } catch {
      replacement = null;
    }
    if (!replacement || typeof replacement !== 'string' || replacement === ref.url) continue;
    out = out.slice(0, ref.start) + replacement + out.slice(ref.end);
  }
  return out;
}
